import "./toast.css";
import {
  useCallback,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
} from "react";

type ToastType = "success" | "error";

type Toast = {
  id: number;
  message: string;
  type: ToastType;
  duration: number;
};

type ShowToastOptions = {
  type: ToastType;
  duration?: number;
};
type ToastContextType = {
  showToast: (message: string, options: ShowToastOptions) => void;
};

const ToastContext = createContext<ToastContextType | null>(null);

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const removeToast = useCallback((id: number) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (
      message: string,
      { type = "success", duration }: ShowToastOptions,
    ) => {
      const id = nextId.current++;
      const computedDuration = duration ?? (type === "error" ? 5000 : 3000);

      setToasts((prevToasts) => [
        ...prevToasts,
        { id, message, type, duration: computedDuration },
      ]);

      window.setTimeout(() => {
        removeToast(id);
      }, computedDuration);
    },
    [removeToast],
  );

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="toast__container">
        {toasts.map((toast) => (
        <div
          className={`toast__message toast__animation toast__${toast.type}`}
          key={toast.id}
          style={{
            ["--toast-fadeout-delay" as string]: `${Math.max(toast.duration - 500, 0)}ms`,
          }}
        >
          <span>{toast.message}</span>
          <button
            type="button"
            className="toast__close"
            onClick={() => removeToast(toast.id)}
          >
            x
          </button>
        </div>
      ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
